import type { UpdateRBLSettingsBody } from '@/@types/practices';
import type { AREA_OF_CONCERN } from '@/constants';
import { isModerateOrSevereRblFinding } from '@/utils/rbl';

import { getIsUserAnnotation } from '../../types';
import type {
  FindingObject,
  Models,
  UserAnnotation,
  UserValidation,
} from '../../types';
import type { useGetModelFeatureFlags } from '../../utils';

interface Params {
  findings: FindingObject[] | UserAnnotation[];
  findingType: keyof Models | typeof AREA_OF_CONCERN;
  isPeriapical?: boolean;
  modelFeatureFlags: ReturnType<typeof useGetModelFeatureFlags>;
  rblSetting: UpdateRBLSettingsBody['templateName'];
}

export const filterForUserValidationFindings = ({
  findings,
  findingType,
  isPeriapical = false,
  modelFeatureFlags,
  rblSetting,
}: Params) => {
  const { isPrlEnabled, isRblEnabled, isVdaEnabled, isCariesSubclassEnabled } =
    modelFeatureFlags;

  return findings.filter((finding: FindingObject | UserAnnotation) => {
    const isUserAnnotation = getIsUserAnnotation(finding);

    const visibleUserValidations: UserValidation[] = ['Disagree', 'Monitor'];
    // Filter out system findings that haven't yet received interaction or have been confirmed.
    if (
      !isUserAnnotation &&
      (!finding.validation ||
        !visibleUserValidations.includes(finding.validation))
    ) {
      return false;
    }

    // Filter out findings for which user validation is not enabled
    switch (findingType) {
      case 'calculus':
        return false;
      case 'caries':
        return true;
      case 'periapical_radiolucency':
        return isPrlEnabled;
      case 'restorative_adjacent_lucency':
        return isVdaEnabled;
      case 'area_of_concern':
        return isCariesSubclassEnabled;
      case 'radiographic_boneloss':
        if (!isRblEnabled) return false;

        return (
          isUserAnnotation ||
          isModerateOrSevereRblFinding(
            finding?.distanceMM ?? 0,
            isPeriapical,
            rblSetting,
          )
        );
      default:
        return false;
    }
  });
};
